<template>
  <div>
    <v-dialog v-model="show" max-width="70vh" scrollable>
      <v-card>
        <v-card-title>
          Key Performance Indicators - Selectors
          <v-spacer></v-spacer>
          <v-btn icon @click="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="py-3">
          <v-data-table
            :headers="tableHeaders"
            :items="selectors"
            :items-per-page="10"
          >
            <template v-slot:[`item.Selector_Name`]="{ item, index }">
              <v-text-field
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.Selector_Name"
                dense
                label="Selector Name"
              ></v-text-field>
              <span v-else>{{ item.Selector_Name }}</span>
            </template>

            <template v-slot:[`item.KPI_Type`]="{ item, index }">
              <v-select
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.KPI_Type_ID"
                :items="kpiTypes"
                item-text="KPI_Type"
                item-value="KPI_Type_ID"
                dense
                label="Select KPI Type"
              ></v-select>
              <span v-else>{{ item.KPI_Type }}</span>
            </template>

            <template v-slot:[`item.Actions`]="{ item, index }">
              <v-btn
                :disabled="editMode && (currentRowIndex !== index) || (currentRowIndex === index && canUpdate)"
                :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item, index)"
              >
                <v-icon>
                  {{ currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>

<!--This feature is currently disabled.-->
<!--               <v-btn
                small
                icon
                color="red darken-2"
                v-if="index !== currentRowIndex"
                :disabled="editMode"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
 -->
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="index !== currentRowIndex"
                hint="111"
                :disabled="true"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>

              <v-btn
                v-if="editMode && currentRowIndex === index"
                color="primary"
                small
                icon
                @click="onCancelClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            :disabled="editMode"
            @click="createNewDialog = !createNewDialog"
          >
            Add New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="500" v-model="createNewDialog">
      <v-card>
        <v-card-title>
          New KPI Selector
          <v-spacer></v-spacer>
          <v-btn icon @click="createNewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <v-text-field
            label="Selector Name"
            v-model="newSelectorName"
            dense
          ></v-text-field>
          <v-select
            label="KPI Type"
            v-model="newSelectorType"
            :items="kpiTypes"
            item-text="KPI_Type"
            item-value="KPI_Type_ID"
            dense
          ></v-select>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small color="primary" :disabled="!newSelectorName || !newSelectorType" @click="createSelector">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="deleteDialog"
      v-model="deleteDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteSelector"
      @onCancelClick="cancelDelete"
    />
  </div>
</template>

<script>
export default {
  name: 'KeyPerformanceIndicator',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      currentRowIndex: null,
      currentItem: null,
      selectors: [],
      kpiTypes: [],
      newSelectorName: '',
      newSelectorType: null,
      createNewDialog: false,
      deleteDialog: false,
      editMode: false,
      tableHeaders: [
        { text: 'Selector Name', value: 'Selector_Name', class: 'px-2 text-no-wrap', cellClass: 'px-2 text-no-wrap' },
        { text: 'KPI Type', value: 'KPI_Type', class: 'px-2 text-no-wrap', cellClass: 'px-2 text-no-wrap' },
        { text: 'Actions', value: 'Actions', align: 'center', sortable: false, width: 120 }
      ]
    }
  },
  computed: {
    user () { return this.$store.getters.getUser },
    show: {
      get () { return this.value },
      set (v) { this.$emit('input', v) }
    },
    canUpdate () {
      return !this.currentItem.Selector_Name || !this.currentItem.KPI_Type_ID
    },
    confirmOrDeniedDialogModes () { return this.$store.getters.getConfirmOrDeniedDialogModes }
  },
  mounted () {
    this.getKpiTypes().then(() => {
      this.getSelectors()
    })
  },
  methods: {
    async getSelectors (kpiTypeId = null) {
      try {
        const params = {}
        if (kpiTypeId !== null) {
          params.KPI_Type_ID = kpiTypeId
        }
        const res = await this.axios.get('/api/kpi_selectors', {
          params,
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.selectors = res.data
      } catch (err) {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      }
    },
    async getKpiTypes () {
      try {
        const res = await this.axios.get('/api/kpi_types', {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.kpiTypes = res.data
        await this.enrichSelectorsWithTypeIds()
      } catch (err) {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      }
    },
    enrichSelectorsWithTypeIds () {
      this.selectors = this.selectors.map(selector => {
        const kpiType = this.kpiTypes.find(type => type.KPI_Type === selector.KPI_Type)
        return {
          ...selector,
          KPI_Type_ID: kpiType ? kpiType.KPI_Type_ID : null
        }
      })
    },
    onEditClick (item, index) {
      if (this.editMode) {
        this.updateSelector()
      } else {
        this.currentItem = {
          ...item,
          KPI_Type_ID: item.KPI_Type_ID
        }
        this.currentRowIndex = index
      }
      this.editMode = !this.editMode
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.currentRowIndex = null
    },
    onDeleteClick (item) {
      this.deleteDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteDialog = false
      this.currentItem = null
    },
    createSelector () {
      this.axios.post('/api/kpi_selector', {
        Selector_Name: this.newSelectorName,
        KPI_Type_ID: this.newSelectorType
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.createNewDialog = false
        this.newSelectorName = ''
        this.newSelectorType = null
        this.getSelectors()
        this.$store.dispatch('setSuccess', { successMsg: 'New KPI Selector created' })
      }).catch(err => {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      })
    },
    updateSelector () {
      this.axios.put(`/api/kpi_selector/${this.currentItem.KPI_Selector_ID}`, {
        Selector_Name: this.currentItem.Selector_Name,
        KPI_Type_ID: this.currentItem.KPI_Type_ID
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.getSelectors()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'KPI Selector updated' })
      }).catch(err => {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      })
    },
    deleteSelector () {
      this.axios.delete(`/api/kpi_selector/${this.currentItem.KPI_Selector_ID}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.cancelDelete()
        this.getSelectors()
        this.$store.dispatch('setSuccess', { successMsg: 'KPI Selector deleted' })
      }).catch(err => {
        this.$store.dispatch('setError', { errorMsg: err.message + ' - ' + err.response.data })
      })
    }
  }
}
</script>
